import { useState } from 'react';
import Swal from 'sweetalert2';
import { supabase } from '@/lib/supabaseClient';
import { logAudit } from '@/lib/logAudit';
import { clearModuleCache } from '@/lib/hasModuleAccess';
import type { TenantInfo, SaasModule, TenantModuleRow } from '../page';

interface Props {
  tenant: TenantInfo;
  modules: SaasModule[];
  planSet: Set<string>;
  tmMap: Record<string, TenantModuleRow>;
  onTmMapChange: (map: Record<string, TenantModuleRow>) => void;
}

const SW = { background: '#ffffff', color: '#0f172a', confirmButtonColor: '#2563eb' };

const CATEGORIES = ['Production', 'Website', 'Automation', 'Settings'];

const CAT_STYLE: Record<string, { dot: string; label: string; icon_bg: string; icon_text: string }> = {
  Production: { dot: 'bg-emerald-400', label: 'text-emerald-600', icon_bg: 'bg-emerald-50', icon_text: 'text-emerald-600' },
  Website:    { dot: 'bg-cyan-400',    label: 'text-cyan-600',    icon_bg: 'bg-cyan-50',    icon_text: 'text-cyan-600' },
  Automation: { dot: 'bg-amber-400',   label: 'text-amber-600',   icon_bg: 'bg-amber-50',   icon_text: 'text-amber-600' },
  Settings:   { dot: 'bg-rose-400',    label: 'text-rose-600',    icon_bg: 'bg-rose-50',    icon_text: 'text-rose-600' },
};

const MODULE_ICONS: Record<string, string> = {
  clients: 'ri-user-3-line', contracts: 'ri-file-text-line', payments: 'ri-bank-card-line',
  jobs: 'ri-briefcase-line', public_website: 'ri-global-line', client_portal: 'ri-user-shared-line',
  basic_branding: 'ri-palette-line', digital_invitations: 'ri-mail-send-line', rsvp: 'ri-calendar-check-line',
  media_packages: 'ri-folder-image-line', dvd_online: 'ri-disc-line', photo_selection: 'ri-image-line',
  final_deliveries: 'ri-send-plane-line', ai_sales_assistant: 'ri-robot-line', appointments: 'ri-calendar-line',
  whatsapp_notifications: 'ri-whatsapp-line', email_automations: 'ri-mail-settings-line',
  custom_domain: 'ri-earth-line', extra_storage: 'ri-hard-drive-line',
};

const SOURCE_OPTS = ['plan', 'addon', 'manual', 'trial'];
const SOURCE_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  plan:   { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Plan' },
  addon:  { bg: 'bg-amber-50',   text: 'text-amber-700',   label: 'Add-on' },
  manual: { bg: 'bg-sky-50',     text: 'text-sky-700',     label: 'Manual' },
  trial:  { bg: 'bg-rose-50',    text: 'text-rose-700',    label: 'Trial' },
};

export default function ModulesTab({ tenant, modules, planSet, tmMap, onTmMapChange }: Props) {
  const [toggling, setToggling] = useState<string | null>(null);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const isEnabled = (id: string) => tmMap[id] ? tmMap[id].is_enabled : planSet.has(id);
  const getSource = (id: string) => tmMap[id] ? tmMap[id].source : planSet.has(id) ? 'plan' : '';
  const getPrice  = (mod: SaasModule) => tmMap[mod.id] ? Number(tmMap[mod.id].price_monthly) : planSet.has(mod.id) ? 0 : mod.price_monthly;

  const upsert = async (mod: SaasModule, patch: Partial<TenantModuleRow>) => {
    const current: TenantModuleRow = tmMap[mod.id] ?? {
      tenant_id: tenant.id, module_id: mod.id,
      is_enabled: planSet.has(mod.id), source: planSet.has(mod.id) ? 'plan' : 'addon',
      price_monthly: planSet.has(mod.id) ? 0 : mod.price_monthly,
    };
    const next = { ...current, ...patch, tenant_id: tenant.id, module_id: mod.id, updated_at: new Date().toISOString() };
    const { error } = await supabase.from('tenant_modules').upsert(next, { onConflict: 'tenant_id,module_id' });
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return false; }
    onTmMapChange({ ...tmMap, [mod.id]: next });
    // Bust module cache so tenant side reflects change within next TTL window
    clearModuleCache();
    return true;
  };

  // ── Actions ──────────────────────────────────────────────────────────────────
  const handleToggle = async (mod: SaasModule) => {
    if (toggling) return;
    setToggling(mod.id);
    const newVal = !isEnabled(mod.id);
    await upsert(mod, { is_enabled: newVal });
    logAudit({
      tenantId: tenant.id,
      action: newVal ? 'module_activated' : 'module_deactivated',
      entity: 'module',
      entityId: mod.id,
      entityName: mod.name,
      details: { source: getSource(mod.id), category: mod.category },
    }).catch(() => {});
    setToggling(null);
  };

  const handleEditSource = async (mod: SaasModule) => {
    const opts = SOURCE_OPTS.reduce<Record<string, string>>((acc, s) => {
      acc[s] = SOURCE_BADGE[s]?.label ?? s;
      return acc;
    }, {});
    const { value: newSrc } = await Swal.fire({
      title: `Origen — ${mod.name}`, input: 'select', inputOptions: opts,
      inputValue: getSource(mod.id) || 'addon',
      showCancelButton: true, confirmButtonText: 'Guardar', cancelButtonText: 'Cancelar', ...SW,
    });
    if (!newSrc) return;
    await upsert(mod, { source: newSrc });
  };

  const handleEditPrice = async (mod: SaasModule) => {
    const { value: newPrice } = await Swal.fire({
      title: `Precio mensual — ${mod.name}`,
      input: 'number', inputValue: getPrice(mod),
      inputAttributes: { min: '0', step: '1' },
      showCancelButton: true, confirmButtonText: 'Guardar', cancelButtonText: 'Cancelar', ...SW,
    });
    if (newPrice === undefined || newPrice === null || newPrice === '') return;
    await upsert(mod, { price_monthly: Number(newPrice) });
  };

  // ── Stats ────────────────────────────────────────────────────────────────────
  const enabledCount = modules.filter((m) => isEnabled(m.id)).length;
  const planCount    = modules.filter((m) => planSet.has(m.id) && isEnabled(m.id)).length;
  const addonCount   = modules.filter((m) => !planSet.has(m.id) && isEnabled(m.id)).length;
  const extraMRR     = modules.filter((m) => !planSet.has(m.id) && isEnabled(m.id)).reduce((s, m) => s + getPrice(m), 0);

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Activos total',  value: `${enabledCount}/${modules.length}`, icon: 'ri-apps-line',              color: 'text-slate-900',   bg: 'bg-slate-100' },
          { label: 'Del plan',       value: planCount,                            icon: 'ri-shield-check-line',      color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Add-ons extra',  value: addonCount,                           icon: 'ri-puzzle-line',            color: 'text-amber-600',   bg: 'bg-amber-50' },
          { label: 'Extra/mes',      value: `$${extraMRR}`,                       icon: 'ri-money-dollar-circle-line',color: 'text-sky-600',    bg: 'bg-sky-50' },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
            <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${s.bg} ${s.color} shrink-0`}>
              <i className={`${s.icon} text-base`}></i>
            </span>
            <div>
              <p className={`text-xl font-bold leading-none ${s.color}`}>{s.value}</p>
              <p className="text-slate-400 text-xs mt-1">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Plan info */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${tenant.plan_code === 'enterprise' ? 'bg-emerald-50 border-emerald-200' : tenant.plan_code === 'pro' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
        <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-slate-200 text-slate-500">
          <i className="ri-vip-crown-line text-sm"></i>
        </span>
        <p className="text-slate-600 text-sm">
          Plan <strong className="text-slate-900 uppercase">{tenant.plan_code}</strong>
          {' '}· {planSet.size} módulos incluidos automáticamente
          <span className="text-slate-400 text-xs ml-2">Clic en "origen" o "precio" para editar inline</span>
        </p>
      </div>

      {/* Modules table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[1fr_90px_80px_80px_56px] border-b border-slate-200 bg-slate-50">
          {['Módulo', 'Origen', 'Tipo', 'Precio/mes', 'On'].map((h, i) => (
            <div key={h} className={`px-4 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider ${i === 4 ? 'text-center' : ''}`}>{h}</div>
          ))}
        </div>

        {CATEGORIES.map((cat) => {
          const catMods = modules.filter((m) => m.category === cat);
          if (catMods.length === 0) return null;
          const cs = CAT_STYLE[cat] ?? CAT_STYLE.Production;
          return (
            <div key={cat}>
              {/* Category header */}
              <div className="grid grid-cols-[1fr_90px_80px_80px_56px] border-b border-slate-100 bg-slate-50/60">
                <div className="col-span-5 px-4 py-2 flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${cs.dot}`}></span>
                  <span className={`text-[11px] font-bold uppercase tracking-wider ${cs.label}`}>{cat}</span>
                </div>
              </div>

              {catMods.map((mod, idx) => {
                const enabled  = isEnabled(mod.id);
                const source   = getSource(mod.id);
                const price    = getPrice(mod);
                const sb       = SOURCE_BADGE[source];
                const icon     = MODULE_ICONS[mod.id] ?? 'ri-apps-line';
                const spinning = toggling === mod.id;

                return (
                  <div
                    key={mod.id}
                    className={`grid grid-cols-[1fr_90px_80px_80px_56px] items-center ${idx < catMods.length - 1 ? 'border-b border-slate-100' : ''} hover:bg-slate-50/50 transition-colors`}
                  >
                    {/* Name */}
                    <div className="px-4 py-3 flex items-center gap-3">
                      <span className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${cs.icon_bg} ${cs.icon_text}`}>
                        <i className={`${icon} text-sm`}></i>
                      </span>
                      <div>
                        <p className={`text-sm font-medium ${enabled ? 'text-slate-800' : 'text-slate-400'}`}>{mod.name}</p>
                        {mod.is_core && <span className="text-[10px] text-emerald-600">Core</span>}
                      </div>
                    </div>

                    {/* Source — clickable */}
                    <div className="px-3 py-3">
                      {sb ? (
                        <button
                          onClick={() => handleEditSource(mod)}
                          className={`px-2 py-0.5 rounded-md text-[10px] font-semibold cursor-pointer hover:opacity-70 transition-opacity ${sb.bg} ${sb.text}`}
                          title="Clic para cambiar origen"
                        >
                          {sb.label}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleEditSource(mod)}
                          className="text-slate-300 text-xs cursor-pointer hover:text-slate-500 transition-colors"
                        >—</button>
                      )}
                    </div>

                    {/* Type */}
                    <div className="px-3 py-3">
                      {mod.is_core
                        ? <span className="text-[10px] font-medium text-emerald-600">Core</span>
                        : <span className="text-[10px] font-medium text-amber-600">Add-on</span>
                      }
                    </div>

                    {/* Price — clickable */}
                    <div className="px-3 py-3">
                      <button
                        onClick={() => handleEditPrice(mod)}
                        className={`text-sm font-semibold cursor-pointer hover:underline transition-colors ${enabled && price > 0 ? 'text-amber-600' : 'text-slate-300'}`}
                        title="Clic para editar precio"
                      >
                        {price > 0 ? `$${price}` : '—'}
                      </button>
                    </div>

                    {/* Toggle */}
                    <div className="px-3 py-3 flex justify-center">
                      <button
                        onClick={() => handleToggle(mod)}
                        disabled={!!spinning}
                        className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer disabled:opacity-60 ${enabled ? 'bg-blue-600' : 'bg-slate-200'}`}
                      >
                        {spinning ? (
                          <span className="absolute inset-0 flex items-center justify-center">
                            <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                          </span>
                        ) : (
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${enabled ? 'left-5' : 'left-0.5'}`} />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}

        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
          <p className="text-slate-400 text-xs">{enabledCount} de {modules.length} módulos activos</p>
          {extraMRR > 0 && (
            <p className="text-amber-600 text-xs">
              Add-ons activos: <strong>${extraMRR}/mes</strong>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
